import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { feeCategoriesApi, scholarshipsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { ApprovalStatus, Scholarship } from '@/types/finance';
import type { UUID } from '@/types/domain';
import { formatDate, orDash } from '@/utils/format';
import { ApprovalBadge, Money } from './shared';
import { ApprovalDialog } from './ApprovalDialog';
import { StudentPicker } from './StudentPicker';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Financial aid awarded to a child.
 *
 * <p>Separate from discounts because it is a different act: an award has a name
 * families are told, it is reported on separately, and when a bill cannot carry
 * both it is the discount that gives way. A full scholarship is simply 100%.
 */
export function ScholarshipsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('createdAt,desc');

  const [approvalFilter, setApprovalFilter] = useState<ApprovalStatus | ''>('');
  const [open, setOpen] = useState(false);
  const [deciding, setDeciding] = useState<Scholarship | null>(null);
  const [student, setStudent] = useState<{ id: UUID; name: string; number?: string } | null>(null);
  const [form, setForm] = useState({
    name: '', awardType: 'PERCENTAGE', value: '', maxAmount: '',
    recurrence: 'RECURRING', startDate: today(), endDate: '',
    reason: '', notes: '', feeCategoryIds: [] as UUID[],
  });

  const params = {
    ...table.params,
    ...(approvalFilter !== '' ? { approvalStatus: approvalFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.scholarships.list(params),
    queryFn: () => scholarshipsApi.list(params),
  });

  const categories = useQuery({
    queryKey: queryKeys.feeCategories.list(true),
    queryFn: () => feeCategoriesApi.list(true),
    enabled: open,
  });

  const propose = useMutation({
    mutationFn: () =>
      scholarshipsApi.create({
        name: form.name,
        studentId: student?.id as UUID,
        awardType: form.awardType,
        value: Number(form.value),
        ...(form.maxAmount !== '' ? { maxAmount: Number(form.maxAmount) } : {}),
        recurrence: form.recurrence,
        startDate: form.startDate,
        ...(form.endDate !== '' ? { endDate: form.endDate } : {}),
        reason: form.reason,
        ...(form.notes !== '' ? { notes: form.notes } : {}),
        ...(form.feeCategoryIds.length > 0 ? { feeCategoryIds: form.feeCategoryIds } : {}),
      }),
    onSuccess: () => {
      toast.success('Proposed — it reduces nothing until awarded');
      setOpen(false);
      setStudent(null);
      setForm({
        name: '', awardType: 'PERCENTAGE', value: '', maxAmount: '', recurrence: 'RECURRING',
        startDate: today(), endDate: '', reason: '', notes: '', feeCategoryIds: [],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scholarships.all });
    },
    onError: (error) => handleApiError(error),
  });

  const decide = useMutation({
    mutationFn: ({ id, approved, reason }: { id: string; approved: boolean; reason: string }) =>
      scholarshipsApi.decide(id, { approved, ...(reason.trim() !== '' ? { reason } : {}) }),
    onSuccess: (updated) => {
      toast.success(updated.approvalStatus === 'APPROVED' ? 'Scholarship awarded' : 'Scholarship refused');
      setDeciding(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.scholarships.all });
    },
    onError: (error) => { setDeciding(null); handleApiError(error); },
  });

  const columns: Column<Scholarship>[] = [
    {
      key: 'name',
      header: 'Award',
      sortKey: 'name',
      render: (s) => (
        <div>
          <span className="font-medium text-ink">{s.name}</span>
          <span className="block text-[11px] text-muted">
            {s.recurrence === 'RECURRING' ? 'every invoice in its dates' : 'one invoice only'}
          </span>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (s) => (
        <div>
          <span className="text-ink">{orDash(s.studentName)}</span>
          <span className="block text-[11px] text-muted">
            {s.feeCategoryNames.length === 0 ? 'every fee' : s.feeCategoryNames.join(', ')}
          </span>
        </div>
      ),
    },
    {
      key: 'value',
      header: 'Worth',
      sortKey: 'value',
      className: 'text-right',
      render: (s) => (
        <div className="text-right">
          <span className="block tabular-nums text-ink">
            {s.awardType === 'PERCENTAGE' ? `${s.value}%` : s.value.toFixed(2)}
          </span>
          {s.fullScholarship && <span className="block text-[10.5px] text-success">full award</span>}
        </div>
      ),
    },
    {
      key: 'awarded',
      header: 'Awarded so far',
      className: 'text-right',
      render: (s) => (
        <div>
          <Money amount={s.totalAwarded} />
          <span className="block text-right text-[10.5px] text-subtle">
            across {s.timesApplied} invoice{s.timesApplied === 1 ? '' : 's'}
          </span>
        </div>
      ),
    },
    {
      key: 'window',
      header: 'In force',
      sortKey: 'startDate',
      render: (s) => (
        <div className="whitespace-nowrap text-[13px]">
          <span className="tabular-nums text-ink">{formatDate(s.startDate)}</span>
          <span className="block text-[11px] text-muted">
            {s.endDate === undefined ? 'ongoing' : `until ${formatDate(s.endDate)}`}
          </span>
        </div>
      ),
    },
    {
      key: 'approval',
      header: 'Approval',
      render: (s) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <ApprovalBadge status={s.approvalStatus} />
          {s.approvalStatus === 'APPROVED' && !s.inForce && (
            <Badge tone="neutral">not in force</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (s) =>
        s.approvalStatus === 'PENDING' && can(PERMISSIONS.SCHOLARSHIP_APPROVE) ? (
          <Button size="sm" onClick={() => setDeciding(s)}>Decide</Button>
        ) : null,
    },
  ];

  return (
    <>
      <ListPage
        title="Scholarships"
        description="Financial aid awarded to a child. Awarding is a separate decision from proposing, and nothing pending reduces a bill."
        icon={<GraduationCap className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.SCHOLARSHIP_MANAGE) ? (
            <Button onClick={() => setOpen(true)}>Propose an award</Button>
          ) : undefined
        }
        filters={
          <FilterSelect label="Approval" value={approvalFilter}
                        onChange={(value) => { setApprovalFilter(value); table.setPage(0); }}
                        options={[
                          { value: 'PENDING', label: 'Awaiting a decision' },
                          { value: 'APPROVED', label: 'Awarded' },
                          { value: 'REJECTED', label: 'Refused' },
                        ]} />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(s) => s.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No scholarships"
          emptyDescription="Propose an award when a child is given financial aid."
          emptyIcon={<GraduationCap className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={open}
        size="lg"
        title="Propose an award"
        description="A full scholarship is a percentage award of 100 — there is no separate kind."
        onClose={() => { setOpen(false); setStudent(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setOpen(false); setStudent(null); }}>
              Cancel
            </Button>
            <Button loading={propose.isPending}
                    disabled={student === null || form.name === '' || form.value === '' || form.reason === ''}
                    onClick={() => propose.mutate()}>
              Propose
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <StudentPicker value={student} onChange={setStudent} />

          <TextField label="Name of the award" required placeholder="Merit Award 2026"
                     hint="Families are told this name, so make it the one they will hear."
                     value={form.name}
                     onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))} />

          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField label="Worked out as" required value={form.awardType}
                         onChange={(event) => setForm((f) => ({ ...f, awardType: event.target.value }))}>
              <option value="PERCENTAGE">A percentage</option>
              <option value="FIXED">A fixed amount</option>
            </SelectField>
            <TextField label={form.awardType === 'PERCENTAGE' ? 'Percentage' : 'Amount'}
                       type="number" step="0.01" required value={form.value}
                       onChange={(event) => setForm((f) => ({ ...f, value: event.target.value }))} />
            <TextField label="Cap" type="number" step="0.01" value={form.maxAmount}
                       hint="Optional ceiling."
                       onChange={(event) => setForm((f) => ({ ...f, maxAmount: event.target.value }))} />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField label="How often" required value={form.recurrence}
                         onChange={(event) => setForm((f) => ({ ...f, recurrence: event.target.value }))}>
              <option value="RECURRING">Every invoice in its dates</option>
              <option value="ONE_TIME">Once only</option>
            </SelectField>
            <TextField label="From" type="date" required value={form.startDate}
                       onChange={(event) => setForm((f) => ({ ...f, startDate: event.target.value }))} />
            <TextField label="Until" type="date" value={form.endDate} hint="Blank means ongoing."
                       onChange={(event) => setForm((f) => ({ ...f, endDate: event.target.value }))} />
          </div>

          <fieldset>
            <legend className="mb-1.5 text-xs font-medium text-muted">Which fees it covers</legend>
            <p className="mb-2 text-xs text-subtle">
              Choose none to cover every fee. A 50% tuition award takes half the tuition and
              nothing off the transport.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(categories.data ?? []).map((category) => {
                const chosen = form.feeCategoryIds.includes(category.id);
                return (
                  <button key={category.id} type="button"
                          onClick={() => setForm((f) => ({
                            ...f,
                            feeCategoryIds: chosen
                              ? f.feeCategoryIds.filter((id) => id !== category.id)
                              : [...f.feeCategoryIds, category.id],
                          }))}
                          className={`border px-2.5 py-1 text-xs transition-colors ${
                            chosen ? 'border-accent bg-accent-soft text-accent-soft-fg'
                                   : 'border-line bg-surface text-muted hover:border-line-strong'}`}>
                    {category.name}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <TextField label="Reason" required placeholder="Top of the class in the final examination"
                     value={form.reason}
                     onChange={(event) => setForm((f) => ({ ...f, reason: event.target.value }))} />
          <TextField label="Notes" value={form.notes}
                     onChange={(event) => setForm((f) => ({ ...f, notes: event.target.value }))} />
        </div>
      </Modal>

      <ApprovalDialog
        open={deciding !== null}
        onClose={() => setDeciding(null)}
        pending={decide.isPending}
        title={deciding === null ? '' : deciding.name}
        summary={
          deciding === null
            ? ''
            : `${deciding.awardType === 'PERCENTAGE' ? `${deciding.value}%` : deciding.value.toFixed(2)} for ${orDash(deciding.studentName)} — ${deciding.reason}`
        }
        onDecide={(approved, reason) =>
          deciding !== null && decide.mutate({ id: deciding.id, approved, reason })
        }
      />
    </>
  );
}
